import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TableComponent } from '../../../shared/component/table/table.component';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ApiCallsService } from '../../../shared/services/api-calls.service';
import { AgGridTableService } from '../../../shared/services/ag-grid.service';
import { ConnectionService } from '../../../connection.service';
import { FormModalComponent } from '../../../shared/component/form-modal/form-modal.component';
import { environment } from '../../../../environments/environment';
import { ConfigPaginationService } from './configpagination.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ToastService } from '../../../shared/services/toast.service';
import { ReceiptPrintService } from '../../../shared/services/receipt-print.service';

interface Location {
  id: string;
  location_no: string;
}

// One leaf entry from the SideComponentConfig localStorage list
interface SideMenuLeaf {
  title: string;
  url?: string | null;
  api_url?: string;
  category?: string;
  is_view?: boolean;
  is_add?: boolean;
  is_edit?: boolean;
  id?: string;
  _id?: string;
}

// A single column definition from the schema-type response (Data.list.columns)
interface ConfigColumn {
  title: string;
  type: string;
  add?: boolean;
  edit?: boolean;
  map?: any;
  regex?: string;
  [key: string]: any;
}

// Shape consumed by FormModalComponent's [FormArray] input
interface ModalFormField {
  id: string;
  placeholder: string;
  value: any;
  dropdown: boolean;
  apiUrl?: string;
  options?: any[];
  optionKey?: string;
  option_mapping?: { id: string; name: string };
  class?: string;
  regex?: string | null;
}

// ────────────────────────────────────────────────────────────────────────
// DEBUG_TAG: prefix used on every console.log added for debugging so you
// can filter the console by typing "[CFG]" into the devtools filter box.
import { FilterSelectComponent } from '../../../shared/component/filter-select/filter-select.component';

// ────────────────────────────────────────────────────────────────────────
// DEBUG_TAG: prefix used on every console.log added for debugging so you
// can filter the console by typing "[CFG]" into the devtools filter box.
// ────────────────────────────────────────────────────────────────────────
const DEBUG_TAG = '[CFG]';

@Component({
  selector: 'app-configuration',
  imports: [CommonModule, NgbModule, FormsModule, TableComponent, FormModalComponent, ScrollingModule, FilterSelectComponent],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  active: number = 0
  isAtLeft = true;
  isAtRight = false;
  configuration: any[] = [];
  siteId: string = '';
  currentActiveTab: string = ''
  currentActiveTabName: string = ''
  currentActiveEdit: boolean = false;
  currentActiveAdd: boolean = false;
  private subscriptions = new Subscription();
  location: boolean = false
  search: boolean = false
  locationSearchTerm: string = '';
  currentActiveCsv: boolean = false;
  currentActivePdf: boolean = false;
  currentActiveAddRouteUrl: string = '';
  table_header: string = '';
  pageNation: boolean = true;
  // Modal form arrays built from a schema-type response, consumed by FormModalComponent
  addFormArray: ModalFormField[] = [];
  editFormArray: ModalFormField[] = [];
  // Cached per-apiUrl so we don't rebuild forms on every tab revisit
  private formSchemaCache: { [apiUrl: string]: { columns: { [key: string]: ConfigColumn }; add: ModalFormField[]; edit: ModalFormField[] } } = {};

  // single source of truth, usable from component AND passed into the service call
  getModalKey(tab: any): string {
    return tab?.configId || tab?.apiUrl || '';
  }

  constructor(
    private router: Router,
    private apiservice: ApiCallsService,
    private pagination: ConfigPaginationService,
    private itemid: ConnectionService,
    private loader: LoaderService,
    private http: HttpClient,
    private toast: ToastService,
    private aggrid: AgGridTableService,
    private receiptPrintService: ReceiptPrintService
  ) { }

  selectedLocationIds: Location[] = [];

  get filteredLocations(): Location[] {
    if (!this.locationSearchTerm) {
      return this.selectedLocationIds;
    }

    const term = this.locationSearchTerm.toLowerCase();
    return this.selectedLocationIds.filter(loc =>
      loc.location_no?.toLowerCase().includes(term)
    );
  }

  placeholder: string = '';
  appliedFilterPayload: any = {};

  get currentFilterConfig(): any {
    const tab = this.activetab;
    const meta: any = this.pagination.getTableMeta(this.currentActiveTab, tab?.configId || '');
    const f = tab?.filter || meta?.filter || {};
    const flags = f.filterFlags || tab?.filterFlags || meta?.filterFlags || {};

    return {
      enabled: f.enabled !== false,
      showSites: !!(f.showSites ?? flags.showSites ?? tab?.showSites ?? meta?.showSites ?? false),
      SiteOnly: !!(f.SiteOnly ?? flags.SiteOnly ?? tab?.SiteOnly ?? meta?.SiteOnly ?? false),
      showLocation: !!(f.showLocation ?? flags.showLocation ?? f.location ?? tab?.location ?? meta?.location ?? false),
      FromToLocation: !!(f.FromToLocation ?? flags.FromToLocation ?? tab?.FromToLocation ?? meta?.FromToLocation ?? false),
      SingleLocationDropdown: !!(f.SingleLocationDropdown ?? flags.SingleLocationDropdown ?? tab?.SingleLocationDropdown ?? tab?.location ?? false),
      showDaterange: !!(f.showDaterange ?? flags.showDaterange ?? tab?.showDaterange ?? meta?.showDaterange ?? false),
      showSingleDate: !!(f.showSingleDate ?? flags.showSingleDate ?? tab?.showSingleDate ?? meta?.showSingleDate ?? false),
      Month_Year: !!(f.Month_Year ?? flags.Month_Year ?? tab?.Month_Year ?? meta?.Month_Year ?? false),
      Month_Range: !!(f.Month_Range ?? flags.Month_Range ?? tab?.Month_Range ?? meta?.Month_Range ?? false),
      Date_Time: !!(f.Date_Time ?? flags.Date_Time ?? tab?.Date_Time ?? meta?.Date_Time ?? false),
      Date_Flat: !!(f.Date_Flat ?? flags.Date_Flat ?? tab?.Date_Flat ?? meta?.Date_Flat ?? false),
      AllSelection: !!(f.AllSelection ?? flags.AllSelection ?? tab?.AllSelection ?? meta?.AllSelection ?? true),
      showsubtype: !!(f.showsubtype ?? flags.showsubtype ?? tab?.showsubtype ?? meta?.showsubtype ?? false),
      showappliances: !!(f.showappliances ?? flags.showappliances ?? tab?.showappliances ?? meta?.showappliances ?? false),
      showBillNo: !!(f.showBillNo ?? flags.showBillNo ?? tab?.showBillNo ?? meta?.showBillNo ?? false),
      Hours: !!(f.Hours ?? flags.Hours ?? tab?.Hours ?? meta?.Hours ?? false),
      search: !!(f.search ?? flags.search ?? tab?.search ?? meta?.search ?? false),
      placeholder: f.placeholder || tab?.placeholder || meta?.placeholder || 'Search',
      filterConfig: f.filterConfig || flags.filterConfig || tab?.filterConfig || meta?.filterConfig || []
    };
  }

  get hasFilterBar(): boolean {
    if (!this.activetab) return false;
    const cfg = this.currentFilterConfig;
    if (!cfg.enabled) return false;
    return !!(
      cfg.showSites ||
      cfg.SiteOnly ||
      cfg.showLocation ||
      cfg.FromToLocation ||
      cfg.SingleLocationDropdown ||
      cfg.showDaterange ||
      cfg.showSingleDate ||
      cfg.Month_Year ||
      cfg.Month_Range ||
      cfg.Date_Time ||
      cfg.Date_Flat ||
      cfg.showsubtype ||
      cfg.showappliances ||
      cfg.showBillNo ||
      cfg.Hours ||
      cfg.search ||
      (cfg.filterConfig && cfg.filterConfig.length > 0)
    );
  }

  filterAppliedForTab: Record<string, boolean> = {};

  get isLoadAfterFilterActive(): boolean {
    const tab = this.activetab;
    if (!tab) return false;
    const meta: any = this.pagination.getTableMeta(this.currentActiveTab, tab?.configId || '');
    return !!(
      tab.load_after_filter ||
      tab.loadAfterFilter ||
      tab.list?.load_after_filter ||
      tab.list?.loadAfterFilter ||
      tab.filter?.load_after_filter ||
      tab.filter?.loadAfterFilter ||
      meta?.load_after_filter ||
      meta?.loadAfterFilter ||
      meta?.list?.load_after_filter ||
      meta?.list?.loadAfterFilter ||
      meta?.filter?.load_after_filter ||
      meta?.filter?.loadAfterFilter
    );
  }

  get shouldShowTable(): boolean {
    if (!this.isLoadAfterFilterActive) {
      return true;
    }
    const key = this.activetab?.id || this.currentActiveTab;
    return !!this.filterAppliedForTab[key];
  }

  get isReprintReceiptTab(): boolean {
    const meta: any = this.pagination.getTableMeta(this.currentActiveTab, this.activetab?.configId || '');
    const action = (this.activetab?.action || meta?.action || '').toLowerCase();
    const isReprintFlag = !!(
      this.activetab?.is_reprint ||
      meta?.is_reprint ||
      (meta as any)?.enableCheckbox ||
      (this.activetab as any)?.enableCheckbox ||
      meta?.tableAction?.type === 'printReceipt' ||
      this.activetab?.tableAction?.type === 'printReceipt'
    );
    const name = (this.activetab?.name || '').toLowerCase();
    const apiUrl = (this.activetab?.apiUrl || '').toLowerCase();
    const type = (this.activetab?.type || '').toLowerCase();
    return isReprintFlag || action.includes('reprint') || action.includes('receipt') || name.includes('reprint') || name.includes('receipt') || apiUrl.includes('reprint') || type === 'receipt';
  }

  get reprintBadgeText(): string {
    const meta: any = this.pagination.getTableMeta(this.currentActiveTab, this.activetab?.configId || '');
    return meta?.tableAction?.badge || this.activetab?.tableAction?.badge || 'Select receipts to print';
  }

  get reprintButtonLabel(): string {
    const meta: any = this.pagination.getTableMeta(this.currentActiveTab, this.activetab?.configId || '');
    return meta?.tableAction?.label || this.activetab?.tableAction?.label || 'Print';
  }

  printSelectedReceipts(): void {
    const selectedRows = this.aggrid.checkboxHandler([]);
    if (!selectedRows || selectedRows.length === 0) {
      this.toast.showToast('Please select at least one record to print receipt.', 'colored', 3000, 'warning');
      return;
    }

    const headers = this.aggrid.getVisibleColumns();
    const result = this.receiptPrintService.printMultipleReceipts(selectedRows, headers);
    if (!result.success && result.error) {
      this.toast.showToast(result.error, 'colored', 5000, 'danger');
    }
  }

  handleFilterApplied(payload: any): void {
    if (!this.activetab?.apiUrl) return;
    const key = this.activetab?.id || this.currentActiveTab;
    this.filterAppliedForTab[key] = true;
    this.appliedFilterPayload = payload || {};
    this.getTableData(
      this.activetab?.apiUrl,
      this.activetab?.orderby
    );
  }

  handleFilterReset(show: boolean): void {
    if (!this.activetab?.apiUrl) return;
    const key = this.activetab?.id || this.currentActiveTab;
    if (this.isLoadAfterFilterActive) {
      this.filterAppliedForTab[key] = false;
      this.appliedFilterPayload = {};
      this.aggrid.processTableData([]);
      this.totalcount = 0;
      return;
    }
    this.appliedFilterPayload = {};
    this.getTableData(
      this.activetab?.apiUrl,
      this.activetab?.orderby
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.ConsumerLocationId = ''
    this.locationSearchTerm = ''
    this.searchString = ''
    this.selectedLocation = null
  }

  getLocationList(siteid: string | null = '') {
    const site_id = siteid
    if (site_id) {
      this.apiservice.getLocationList(site_id).subscribe(res => {
        let data: any[] = [];
        data = [
          { id: 'ALL', location_no: 'All Units' },
          ...(res?.Data || [])
        ];

        this.selectedLocationIds = data;
        const allOption = data.find(d => d.id === 'ALL');

        this.selectedLocation = allOption ?? data[0] ?? null;
      });
    }
  }

  tableitemID: string = ''

  ngOnInit(): void {
    // Modal form fields — independent of edit-row clicks, must be live from the start
    this.subscriptions.add(
      this.pagination.formSchema$.subscribe(schema => {
        console.log(`${DEBUG_TAG} formSchema$ emitted ->`, schema);
        this.addFormArray = schema?.add || [];
        this.editFormArray = schema?.edit || [];
        console.log(`${DEBUG_TAG} addFormArray/editFormArray set from formSchema$:`, {
          addFormArray: this.addFormArray,
          editFormArray: this.editFormArray
        });
      })
    );

    this.subscriptions.add(
      this.itemid.editData$.subscribe((rowData: any) => {
        console.log(`${DEBUG_TAG} editData$ row clicked ->`, rowData);
        if (!rowData) return;
        // 🔹 Find the currently active config tab
        const activeConfig = this.configuration.find(x => x.apiUrl === this.currentActiveTab);
        if (!activeConfig) return;
        // store row id
        this.tableitemID = rowData.id || '';

        // Populate each edit field's `value` from the clicked row, matching by
        // field.id -> rowData[field.id]. Base fields come from whichever source
        // is currently active for this tab — schema-driven (Table-Configuration,
        // via formSchema$) or the old ConfigComponents modalFields — same shape
        // either way, so the mapping logic doesn't need to care which one it is.
        const baseFields = this.editFormArray.length ? this.editFormArray : activeConfig.modalFields;
        console.log(`${DEBUG_TAG} EDIT form base fields source:`,
          this.editFormArray.length ? 'editFormArray (schema-driven)' : 'activeConfig.modalFields (legacy)');

        this.editFormArray = (baseFields || []).map((field: any) => {
          const matchKey = field.matchKey || field.options?.matchKey;
          let val = (matchKey && rowData[matchKey] !== undefined) ? rowData[matchKey] :
            (rowData[field.id] !== undefined ? rowData[field.id] :
              (field.optionKey && rowData[field.optionKey] !== undefined ? rowData[field.optionKey] :
                (field.value ?? '')));

          if (field.type === 'date' && field.readonly) {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            val = field.monthYear ? `${year}-${month}` : `${year}-${month}-${day}`;
          }

          // If dropdown options are loaded on this field, normalize value to matching option's ID
          if (field.dropdown && Array.isArray(field.options) && field.options.length && val !== '' && val !== null && val !== undefined) {
            const strVal = String(val).trim().toLowerCase();
            const match = field.options.find((opt: any) =>
              (opt.id !== undefined && String(opt.id).trim().toLowerCase() === strVal) ||
              (opt.name !== undefined && String(opt.name).trim().toLowerCase() === strVal) ||
              (matchKey && opt[matchKey] !== undefined && String(opt[matchKey]).trim().toLowerCase() === strVal) ||
              (field.optionKey && opt[field.optionKey] !== undefined && String(opt[field.optionKey]).trim().toLowerCase() === strVal)
            );
            if (match) {
              val = match.id;
            }
          }

          return {
            ...field,
            value: val
          };
        });

        console.log(`${DEBUG_TAG} EDIT form fields populated with row values ->`, this.editFormArray);
      })
    );

    this.subscriptions.add(
      this.pagination.totalCount$.subscribe(res => {
        this.totalcount = res
      })
    )
    this.updateConfigFromRoute(); // load first time
    this.subscriptions.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => {
          this.updateConfigFromRoute();
        }));
  }

  ngAfterViewInit() {
    setTimeout(() => this.updateEdgeState());
  }

  @HostListener('window:resize')
  onResize() {
    this.updateEdgeState();
  }

  // ──────────────────────────────────────────────────────────────────────
  // Merges a ConfigComponents tab with its matching SideComponentConfig
  // leaf (from localStorage).
  // ──────────────────────────────────────────────────────────────────────
  private mergeTabWithLeaf(tab: any, leafMap: Map<string, SideMenuLeaf>): any {
    const leaf = this.findLeafForTab(leafMap, tab.name);
    const apiUrl = leaf?.api_url ? this.stripLeadingSlash(leaf.api_url) : tab.apiUrl;
    const configId = leaf?.id ?? leaf?._id ?? tab.id ?? '';

    const merged = {
      ...tab,
      apiUrl,
      configId,
      isView: leaf ? !!leaf.is_view : false,
      add: leaf && leaf.is_add !== undefined ? !!leaf.is_add : tab.add,
      edit: leaf && leaf.is_edit !== undefined ? !!leaf.is_edit : tab.edit
    };

    if (!leaf) {
      console.log(
        `${DEBUG_TAG} [mergeTabWithLeaf] NO localStorage match for tab "${tab.name}" ` +
        `-> isView forced to FALSE, tab will be hidden/ignored.`
      );
    } else {
      console.log(
        `${DEBUG_TAG} [mergeTabWithLeaf] tab "${tab.name}" matched leaf "${leaf.title}" ->`,
        { apiUrl, configId, isView: merged.isView, add: merged.add, edit: merged.edit, rawLeaf: leaf }
      );
    }

    return merged;
  }

  private getVisibleLeafList(node: any, list: SideMenuLeaf[] = []): SideMenuLeaf[] {
    if (!node || typeof node !== 'object') return list;

    if (Array.isArray(node)) {
      for (const item of node) {
        if (item && typeof item === 'object') {
          if ('url' in item || 'category' in item || 'is_view' in item || 'api_url' in item || 'title' in item) {
            list.push(item as SideMenuLeaf);
          } else {
            this.getVisibleLeafList(item, list);
          }
        }
      }
      return list;
    }

    for (const key in node) {
      const value = node[key];
      if (value && typeof value === 'object' && ('url' in value || 'category' in value || 'is_view' in value || 'api_url' in value || 'title' in value)) {
        list.push({ ...value, title: value.title || key } as SideMenuLeaf);
      } else if (value && typeof value === 'object') {
        this.getVisibleLeafList(value, list);
      }
    }
    return list;
  }

  private getCurrentLeafList(): SideMenuLeaf[] {
    const sideMenuList = JSON.parse(localStorage.getItem('SideComponentConfig') || '[]');
    const leafList = this.getVisibleLeafList(sideMenuList);
    console.log(`${DEBUG_TAG} [getCurrentLeafList] SideComponentConfig leaves found:`, leafList.map(l => l.title));
    return leafList;
  }

  private getCurrentLeafMap(): Map<string, SideMenuLeaf> {
    const sideMenuList = JSON.parse(localStorage.getItem('SideComponentConfig') || '[]');
    const leafMap = this.getVisibleLeafMap(sideMenuList);
    console.log(`${DEBUG_TAG} [getCurrentLeafMap] SideComponentConfig leaves found:`, Array.from(leafMap.keys()));
    return leafMap;
  }

  activetab: any
  searchString: string = '';

  onTabChange(event: any) {
    console.log(`${DEBUG_TAG} [onTabChange] tab change requested, nextId =`, event.nextId);

    const rawSelected = this.configuration.find(x => x.id === event.nextId);
    if (!rawSelected) {
      console.log(`${DEBUG_TAG} [onTabChange] no configuration entry found for id`, event.nextId);
      return;
    }

    // Re-merge against the CURRENT SideComponentConfig leaf map — not just the
    // cached configuration entry — so a sidebar/localStorage change picked up
    // mid-session (without a full getConfigFile reload) still applies.
    const leafMap = this.getCurrentLeafMap();
    const selected = this.mergeTabWithLeaf(rawSelected, leafMap);

    // Keep the configuration array's entry in sync, since AddData/EditData/
    // getTableData all read off this.activetab / this.configuration later.
    const idx = this.configuration.findIndex(x => x.id === selected.id);
    if (idx !== -1) this.configuration[idx] = selected;

    this.ConsumerLocationId = ''
    this.locationSearchTerm = ''
    this.searchString = ''
    this.selectedLocation = null
    this.loader.show()
    this.currentActiveTabName = selected.name
    this.currentActiveEdit = selected?.edit
    this.currentActiveAdd = selected?.add
    this.currentActiveCsv = selected?.download?.csv ?? false;
    this.currentActivePdf = selected?.download?.pdf ?? false;
    this.currentActiveAddRouteUrl = '';
    this.table_header = selected?.name || '';

    console.log(`${DEBUG_TAG} [onTabChange] settings applied from mergeTabWithLeaf for "${selected.name}":`, {
      apiUrl: selected.apiUrl,
      configId: selected.configId,
      add: this.currentActiveAdd,
      edit: this.currentActiveEdit,
      csv: this.currentActiveCsv,
      pdf: this.currentActivePdf
    });

    this.activetab = selected

    const tabMeta = this.pagination.getTableMeta(this.currentActiveTab, selected?.configId || '');
    if (tabMeta && (tabMeta as any).pagination !== undefined) {
      this.pageNation = (tabMeta as any).pagination;
    } else if (selected?.pagination !== undefined) {
      this.pageNation = selected.pagination;
    } else if (selected.apiUrl === 'SitesList') {
      this.pageNation = false;
    } else {
      this.pageNation = true;
    }

    const filterObj = selected?.filter;
    if (filterObj) {
      this.location = !!(filterObj.location || filterObj.showLocation || selected.location);
      this.search = !!(filterObj.search || selected.search);
      if (this.search) {
        this.placeholder = selected.placeholder || filterObj.placeholder || 'Search...';
      }
    } else {
      this.location = !!selected?.location;
      this.search = !!selected?.search;
      if (this.search) {
        this.placeholder = selected.placeholder || 'Search...';
      }
    }

    if (this.location) {
      this.getLocationList(this.siteId);
    }

    setTimeout(() => {
      this.getTableData(selected.apiUrl, selected.orderby);
    }, 1000);
  }

  // Recursively collects every leaf in the sidemenu keyed by title/name
  private getVisibleLeafMap(node: any, map: Map<string, SideMenuLeaf> = new Map()): Map<string, SideMenuLeaf> {
    if (!node || typeof node !== 'object') return map;

    if (Array.isArray(node)) {
      for (const item of node) {
        if (item && typeof item === 'object') {
          if ('url' in item || 'category' in item || 'is_view' in item || 'api_url' in item || 'title' in item) {
            const title = item.title || item.name || '';
            if (title) map.set(title, item as SideMenuLeaf);
          } else {
            this.getVisibleLeafMap(item, map);
          }
        }
      }
      return map;
    }

    for (const key in node) {
      const value = node[key];
      if (value && typeof value === 'object' && ('url' in value || 'category' in value || 'is_view' in value || 'api_url' in value || 'title' in value)) {
        const title = value.title || key;
        map.set(title, value as SideMenuLeaf);
      } else if (value && typeof value === 'object') {
        this.getVisibleLeafMap(value, map);
      }
    }
    return map;
  }

  // Matches a ConfigComponents tab (`tab.name`) to its counterpart leaf in the
  // new SideComponentConfig structure (`leaf.title`).
  private findLeafForTab(leafMap: Map<string, SideMenuLeaf>, tabName: string): SideMenuLeaf | undefined {
    if (!tabName) return undefined;
    if (leafMap.has(tabName)) return leafMap.get(tabName);

    const target = tabName.trim().toLowerCase();
    for (const [title, leaf] of leafMap.entries()) {
      if (title.trim().toLowerCase() === target) return leaf;
      if (leaf.api_url && this.stripLeadingSlash(leaf.api_url).trim().toLowerCase() === target) return leaf;
    }
    return undefined;
  }

  // Matches a localStorage leaf to its counterpart tab in ConfigComponents API response
  private findTabForLeaf(allTabs: any[], leaf: SideMenuLeaf): any | undefined {
    if (!leaf) return undefined;
    const leafTitle = (leaf.title || '').trim().toLowerCase();
    const leafApi = this.stripLeadingSlash(leaf.api_url || '').trim().toLowerCase();
    const leafUrl = this.stripLeadingSlash(leaf.url || '').trim().toLowerCase();

    for (const tab of allTabs) {
      const tabName = (tab.name || '').trim().toLowerCase();
      const tabApi = this.stripLeadingSlash(tab.apiUrl || '').trim().toLowerCase();

      if (leafTitle && tabName && leafTitle === tabName) return tab;
      if (leafApi && tabApi && leafApi === tabApi) return tab;
      if (leafUrl && tabApi && leafUrl === tabApi) return tab;
    }
    return undefined;
  }

  getConfigFile(type: string): void {
    const leafList = this.getCurrentLeafList();
    const leafMap = this.getCurrentLeafMap();

    console.log(`${DEBUG_TAG} [getConfigFile] raw leaves from localStorage SideComponentConfig:`, leafList.map((l: any) => l.title));

    // ── PREFERENCE: If localStorage items exist, use them directly without calling ConfigComponents API ──
    if (leafList.length > 0) {
      console.log(`${DEBUG_TAG} [getConfigFile] localStorage items found — skipping ConfigComponents API call.`);
      const finalTabs = leafList
        .filter((leaf: any) => leaf && leaf.is_view !== false && !!leaf.is_view)
        .map((leaf: any, index: number) => {
          const apiUrl = leaf.api_url ? this.stripLeadingSlash(leaf.api_url) : (leaf.url ? this.stripLeadingSlash(leaf.url) : '');
          const configId = leaf.id ?? leaf._id ?? `leaf-${index}`;

          return {
            id: configId,
            name: leaf.title || leaf.name || '',
            apiUrl,
            configId,
            isView: true,
            add: leaf.is_add !== undefined ? !!leaf.is_add : (leaf.add ?? false),
            edit: leaf.is_edit !== undefined ? !!leaf.is_edit : (leaf.edit ?? false),
            location: leaf.location ?? ((leaf.filter?.location || leaf.filter?.showLocation) ?? false),
            search: leaf.search ?? (leaf.filter?.search ?? false),
            placeholder: leaf.placeholder ?? (leaf.filter?.placeholder ?? 'Search...'),
            orderby: leaf.orderby ?? '',
            modalFields: leaf.modalFields ?? [],
            download: leaf.download ?? { csv: true, pdf: true },
            filter: leaf.filter
          };
        });

      this.applyConfigurationTabs(finalTabs);
      return;
    }

    // ── Fallback: If no localStorage items are present, call ConfigComponents API ──
    console.log(`${DEBUG_TAG} [getConfigFile] No localStorage items found — calling ConfigComponents("${type}") API.`);
    this.subscriptions.add(this.apiservice.ConfigComponents(type).subscribe(res => {
      const allTabs = res?.Data?.list || [];
      console.log(`${DEBUG_TAG} [getConfigFile] raw tabs from ConfigComponents("${type}"):`, allTabs.map((t: any) => t.name));

      const finalTabs = allTabs
        .map((tab: any) => this.mergeTabWithLeaf(tab, leafMap))
        .filter((tab: any) => tab.isView);

      this.applyConfigurationTabs(finalTabs);
    }));
  }

  private applyConfigurationTabs(finalTabs: any[]): void {
    this.configuration = finalTabs;

    console.log(`${DEBUG_TAG} [applyConfigurationTabs] final tabs to render:`, this.configuration.map((t: any) => ({
      name: t.name, apiUrl: t.apiUrl, add: t.add, edit: t.edit
    })));

    if (this.configuration.length > 0) {
      this.active = this.configuration[0].id;
      this.currentIndex = 0;

      const firstTab = this.configuration[0];
      this.currentActiveTab = firstTab.apiUrl;
      this.currentActiveTabName = firstTab.name;
      this.currentActiveAdd = firstTab?.add;
      this.currentActiveEdit = firstTab?.edit;
      this.currentActiveCsv = firstTab?.download?.csv ?? false;
      this.currentActivePdf = firstTab?.download?.pdf ?? false;
      this.currentActiveAddRouteUrl = '';
      this.table_header = firstTab?.name || '';
      this.activetab = firstTab;

      const firstMeta = this.pagination.getTableMeta(this.currentActiveTab, firstTab?.configId || '');
      if (firstMeta && (firstMeta as any).pagination !== undefined) {
        this.pageNation = (firstMeta as any).pagination;
      } else if (firstTab?.pagination !== undefined) {
        this.pageNation = firstTab.pagination;
      } else if (firstTab.apiUrl === 'SitesList') {
        this.pageNation = false;
      } else {
        this.pageNation = true;
      }

      console.log(`${DEBUG_TAG} [applyConfigurationTabs] initial active tab "${firstTab.name}" settings:`, {
        apiUrl: firstTab.apiUrl, add: this.currentActiveAdd, edit: this.currentActiveEdit
      });

      const firstFilterObj = firstTab?.filter;
      if (firstFilterObj) {
        this.location = !!(firstFilterObj.location || firstFilterObj.showLocation || firstTab.location);
        this.search = !!(firstFilterObj.search || firstTab.search);
        if (this.search) {
          this.placeholder = firstTab.placeholder || firstFilterObj.placeholder || 'Search...';
        }
      } else {
        this.location = !!firstTab?.location;
        this.search = !!firstTab?.search;
        if (this.search) {
          this.placeholder = firstTab.placeholder || 'Search...';
        }
      }

      if (this.location) {
        this.getLocationList(this.siteId);
      }

      this.getTableData(firstTab.apiUrl, firstTab.orderby);
      this.updateEdgeState();
    } else {
      console.log(`${DEBUG_TAG} [applyConfigurationTabs] no visible tabs left after filtering — nothing will render.`);
    }

    this.updateEdgeState();
  }

  private stripLeadingSlash(url: string): string {
    return url ? url.replace(/^\/+/, '') : url;
  }

  // Builds the Add/Edit modal form field arrays from a schema response's
  // Data.list.columns, using each column's `add`/`edit` flags to decide
  // which form(s) it belongs to.
  private buildModalFormFieldsFromColumns(columns: { [key: string]: ConfigColumn }): { add: ModalFormField[]; edit: ModalFormField[] } {
    const add: ModalFormField[] = [];
    const edit: ModalFormField[] = [];

    Object.keys(columns || {}).forEach(key => {
      const col = columns[key];
      if (!col) return;

      const isMap = col.type === 'map';
      const hasDynamicUrl = isMap && !!col.map?.url;

      const field: ModalFormField = {
        id: key,
        placeholder: col.title || key,
        value: '',
        dropdown: isMap,
        class: 'w-100',
        regex: col.regex || null
      };

      if (hasDynamicUrl) {
        // dropdownUrl style — FormModalComponent fetches options from apiUrl,
        // mapping id/name via option_mapping (falls back to field.id for name)
        field.apiUrl = this.stripLeadingSlash(col.map.url);
        field.optionKey = col.map.key || 'id';
        field.option_mapping = { id: col.map.key || 'id', name: col.map.value || 'name' };
        field.options = [];
      } else if (isMap && col.map && typeof col.map === 'object') {
        // static map, e.g. { "D": "Deactive", "M": "Active" }
        field.options = Object.entries(col.map).map(([optKey, optValue]) => ({ id: optKey, name: optValue }));
      }

      if (col.add) add.push(field);
      if (col.edit) edit.push(field);
    });

    console.log(`${DEBUG_TAG} [buildModalFormFieldsFromColumns] built ADD fields:`, add.map(f => f.id));
    console.log(`${DEBUG_TAG} [buildModalFormFieldsFromColumns] built EDIT fields:`, edit.map(f => f.id));

    return { add, edit };
  }

  private buildHttpParams(payload: any): HttpParams {
    let params = new HttpParams();
    Object.keys(payload || {}).forEach(key => {
      const val = payload[key];
      if (val !== null && val !== undefined && val !== '') {
        params = params.set(key, String(val));
      }
    });
    return params;
  }

  // Pre-check call: some responses carry the modal-form schema (Data.list.columns)
  // instead of table rows; others carry normal rows plus optional extra column
  // metadata in a response header. Either way we end by handing off to the
  // existing pagination flow to actually render the table.
  private inspectAndPrepareTableData(apiUrl: string, payload: any) {
    const params = this.buildHttpParams(payload);

    this.subscriptions.add(
      this.http.get<any>(`${environment.apiURL}/${apiUrl}`, { params, observe: 'response' }).subscribe({
        next: (httpRes) => {
          const body: any = httpRes.body;
          const schemaColumns = body?.Data?.list?.columns;
          const isSchemaResponse = !!schemaColumns && typeof schemaColumns === 'object';

          console.log(`${DEBUG_TAG} [inspectAndPrepareTableData] response for "${apiUrl}" isSchemaResponse=${isSchemaResponse}`);

          if (isSchemaResponse) {
            const built = this.buildModalFormFieldsFromColumns(schemaColumns);
            this.formSchemaCache[apiUrl] = { columns: schemaColumns, add: built.add, edit: built.edit };
            this.addFormArray = built.add;
            this.editFormArray = built.edit;
            console.log(`${DEBUG_TAG} [inspectAndPrepareTableData] schema-driven forms cached for "${apiUrl}"`);
          } else {
            // Normal table response — TODO: confirm the actual header name your
            // API uses for extra column metadata; 'x-extra-columns' is a placeholder.
            const extraColumnsHeader = httpRes.headers.get('x-extra-columns');
            if (extraColumnsHeader) {
              try {
                const extraColumns: { [key: string]: ConfigColumn } = JSON.parse(extraColumnsHeader);
                const cached = this.formSchemaCache[apiUrl];
                // Cached schema-response columns win on key conflicts over header-supplied ones
                const mergedColumns = { ...extraColumns, ...(cached?.columns || {}) };
                const built = this.buildModalFormFieldsFromColumns(mergedColumns);
                this.formSchemaCache[apiUrl] = { columns: mergedColumns, add: built.add, edit: built.edit };
                this.addFormArray = built.add;
                this.editFormArray = built.edit;
                console.log(`${DEBUG_TAG} [inspectAndPrepareTableData] merged header columns + cache for "${apiUrl}"`);
              } catch (e) {
                console.error(`${DEBUG_TAG} [inspectAndPrepareTableData] Failed to parse extra-columns header`, e);
              }
            } else if (this.formSchemaCache[apiUrl]) {
              this.addFormArray = this.formSchemaCache[apiUrl].add;
              this.editFormArray = this.formSchemaCache[apiUrl].edit;
              console.log(`${DEBUG_TAG} [inspectAndPrepareTableData] reused cached forms for "${apiUrl}"`);
            }
          }

          this.pagination.initFilters(
            payload,
            this.currentActiveTab,
            '', this.currentActiveTabName, this.activetab?.edit
          );
        },
        error: () => {
          console.log(`${DEBUG_TAG} [inspectAndPrepareTableData] pre-check request failed for "${apiUrl}", falling back to table load without schema.`);
          // Pre-check failed — fall back so the table still loads
          this.pagination.initFilters(
            payload,
            this.currentActiveTab,
            '', this.currentActiveTabName, this.activetab?.edit
          );
        }
      })
    );
  }

  private updateConfigFromRoute() {

    // 1️⃣ Extract URL segments (without query params)
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments.map(s => s.path) || [];

    // 2️⃣ Extract query param: siteid
    const encodedId = urlTree.queryParams['siteid'] || localStorage.getItem('site_idLogin');

    // 3️⃣ Decode siteid safely
    let decodedSiteId = '';
    if (encodedId) {
      try {
        decodedSiteId = atob(encodedId);
      } catch (e) {
        decodedSiteId = '';
      }
    }

    this.siteId = decodedSiteId;

    // 4️⃣ Determine current route (last URL segment)
    const currentRoute = segments.length ? segments[segments.length - 1] : '';

    console.log(`${DEBUG_TAG} [updateConfigFromRoute] route="${currentRoute}" siteId="${this.siteId}"`);

    // 5️⃣ Your existing logic
    this.getConfigFile(currentRoute)
  }

  private updateEdgeState() {
    const container = this.scrollContainer?.nativeElement;
    const items = this.getNavItems();

    if (!container || !items.length) {
      this.isAtLeft = true;
      this.isAtRight = true;
      return;
    }

    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;

    // Check if any item overflows right
    const hasHiddenRight = items.some(item =>
      item.offsetLeft + item.offsetWidth > viewRight + 1
    );

    // Check if any item overflows left
    const hasHiddenLeft = items.some(item =>
      item.offsetLeft < viewLeft - 1
    );

    this.isAtLeft = !hasHiddenLeft;
    this.isAtRight = !hasHiddenRight;
  }

  private encryptSiteId(): string {
    return this.siteId ? btoa(this.siteId) : '';
  }

  /** Routes Add directly when this table has an Add route configured. */
  navigateToAddRoute(): void {
    if (this.currentActiveAddRouteUrl) {
      this.router.navigate([this.currentActiveAddRouteUrl]);
    }
  }

  /** Close an Add/Edit Bootstrap modal only after its API request succeeds. */
  private closeFormModal(action: 'add' | 'edit'): void {
    const modalElement = document.getElementById(`${action}${this.getModalKey(this.activetab)}`);
    if (!modalElement) return;

    const bootstrapModal = (window as any).bootstrap?.Modal;
    const instance = bootstrapModal?.getInstance(modalElement);
    instance?.hide();
  }

  AddData(payload: any, URL: string = '') {
    const configId = this.activetab?.configId || '';

    // If this tab's Add is configured to route elsewhere instead of hitting
    // an API directly, navigate there with the encrypted siteid — don't call
    // TowerDetailsADD at all.
    const addRouteUrl = this.activetab?.add_routeurl;
    if (addRouteUrl) {
      console.log(`${DEBUG_TAG} [AddData] add_routeurl present -> navigating to`, addRouteUrl);
      this.router.navigate([addRouteUrl], {
        queryParams: { siteid: this.encryptSiteId() }
      });
      return;
    }

    const urls = this.pagination.getAddEditUrls(this.activetab?.apiUrl, configId);
    const effectiveUrl = urls?.add || URL;

    const extraPayloads = this.pagination.getAddEditPayloads(this.activetab?.apiUrl, configId);
    const extraAddPayload = extraPayloads?.addPayload;

    console.log(`${DEBUG_TAG} [AddData] tab="${this.activetab?.apiUrl}" configId="${configId}" effectiveUrl="${effectiveUrl}" payload=`, payload);

    let updatedPayload: any = { ...payload, site_id: this.siteId };
    if (extraAddPayload && typeof extraAddPayload === 'object') {
      updatedPayload = { ...extraAddPayload, ...updatedPayload };
    }

    this.subscriptions.add(
      this.apiservice.TowerDetailsADD(effectiveUrl, updatedPayload).subscribe({
        next: res => {
          console.log(`${DEBUG_TAG} [AddData] response ->`, res);
          this.toast.showToast('Data Added Successfully', 'colored', 5000, 'success');
          this.closeFormModal('add');
          this.getTableData(this.activetab?.apiUrl, this.activetab.orderby)
        },
        error: err => {
          console.log(`${DEBUG_TAG} [AddData] error ->`, err);
          this.toast.showToast(err?.error?.Message || err.Message || 'Failed to add data', 'colored', 5000, 'danger');
        }
      }))
  }

  EditData(payload: any, URL: string = '') {
    const configId = this.activetab?.configId || '';

    const editRouteUrl = this.activetab?.edit_routeurl;
    if (editRouteUrl) {
      console.log(`${DEBUG_TAG} [EditData] edit_routeurl present -> navigating to`, editRouteUrl, `rowId=${this.tableitemID}`);
      this.router.navigate([editRouteUrl], {
        queryParams: { siteid: this.encryptSiteId(), id: this.tableitemID }
      });
      return;
    }

    const urls = this.pagination.getAddEditUrls(this.activetab?.apiUrl, configId);
    const effectiveUrl = urls?.edit || URL;

    const extraPayloads = this.pagination.getAddEditPayloads(this.activetab?.apiUrl, configId);
    const extraEditPayload = extraPayloads?.editPayload;

    console.log(`${DEBUG_TAG} [EditData] tab="${this.activetab?.apiUrl}" configId="${configId}" effectiveUrl="${effectiveUrl}" rowId="${this.tableitemID}" payload=`, payload);

    let updatedPayload: any = { ...payload, site_id: this.siteId };
    if (!updatedPayload.id && this.tableitemID) {
      updatedPayload.id = this.tableitemID;
    }
    if (extraEditPayload && typeof extraEditPayload === 'object') {
      updatedPayload = { ...extraEditPayload, ...updatedPayload };
    }

    this.subscriptions.add(this.apiservice.TowerDetailsEdit(effectiveUrl, updatedPayload).subscribe({
      next: res => {
        console.log(`${DEBUG_TAG} [EditData] response ->`, res);
        this.toast.showToast('Data Updated Successfully', 'colored', 5000, 'success');
        this.closeFormModal('edit');
        this.getTableData(this.activetab?.apiUrl, this.activetab.orderby)
      },
      error: err => {
        console.log(`${DEBUG_TAG} [EditData] error ->`, err);
        this.toast.showToast(err?.error?.Message || err.error.Message || 'Failed to update data', 'colored', 5000, 'danger');
      }
    }));
  }

  onColumnFormSubmitted(event: any): void {
    console.log(`${DEBUG_TAG} [onColumnFormSubmitted] event ->`, event);
    const endpoint = (event?.endpoint || '').toLowerCase();
    const isGet = endpoint.includes('get') ||
      event?.payload?.action?.toLowerCase?.() === 'get' ||
      event?.payload?.type?.toLowerCase?.()?.includes?.('get');

    if (isGet) {
      // GET actions are device read commands - do not reload or reset table data
      return;
    }
    this.getTableData(this.activetab?.apiUrl, this.activetab?.orderby);
  }

  getTableData(url: string = '', orderby: string = '') {
    const apiUrl: string = url || this.activetab?.apiUrl;
    if (!apiUrl) {
      console.log(`${DEBUG_TAG} [getTableData] Skipped - no valid apiUrl.`);
      return;
    }
    const orderbyparam: string = orderby || this.configuration[0]?.orderby;
    this.currentActiveTab = apiUrl;
    const configId = this.activetab?.configId || '';

    const tabConfiguredPayload = this.activetab?.payload || this.activetab?.list?.payload || {};
    const rawPayload: any = {
      ...tabConfiguredPayload,
      ...this.appliedFilterPayload
    };

    if (this.siteId) {
      rawPayload.site_id = this.siteId;
      rawPayload.site = this.siteId;
    }
    if (orderbyparam) {
      rawPayload.Order = orderbyparam;
    }
    if (configId) {
      rawPayload.config_id = configId;
    }

    const locId = this.appliedFilterPayload?.loc_id || this.ConsumerLocationId;
    if (locId) rawPayload.location_id = locId;

    const fromLoc = this.appliedFilterPayload?.loc_from || this.appliedFilterPayload?.loc_idFrom || this.appliedFilterPayload?.from_location_id;
    if (fromLoc) rawPayload.from_location_id = fromLoc;

    const toLoc = this.appliedFilterPayload?.loc_to || this.appliedFilterPayload?.loc_idTo || this.appliedFilterPayload?.to_location_id;
    if (toLoc) rawPayload.to_location_id = toLoc;

    const searchVal = this.appliedFilterPayload?.search || this.searchString;
    if (searchVal) rawPayload.search = searchVal;

    const fromD = this.appliedFilterPayload?.from_date || this.appliedFilterPayload?.fromDate;
    if (fromD) {
      rawPayload.from_date = fromD;
      rawPayload.fromDate = fromD;
    }

    const toD = this.appliedFilterPayload?.to_date || this.appliedFilterPayload?.toDate;
    if (toD) {
      rawPayload.to_date = toD;
      rawPayload.toDate = toD;
    }

    const otherT = this.appliedFilterPayload?.other_type || this.appliedFilterPayload?.subtype;
    if (otherT) rawPayload.other_type = otherT;

    const dateVal = this.appliedFilterPayload?.date || this.appliedFilterPayload?.selected_date;
    if (dateVal) rawPayload.date = dateVal;

    const payload: any = {};
    Object.keys(rawPayload).forEach(key => {
      const val = rawPayload[key];
      if (val !== undefined && val !== null && val !== '') {
        payload[key] = val;
      }
    });

    const needsFetch = this.pagination.needsSchemaFetch(apiUrl, configId);
    console.log(`${DEBUG_TAG} [getTableData] apiUrl="${apiUrl}" configId="${configId}" needsSchemaFetch=${needsFetch}`);

    if (needsFetch) {
      this.subscriptions.add(
        this.apiservice.getTableConfig({ parent_id: configId }).subscribe({
          next: (res: any) => {
            this.pagination.setSchemaFromResponse(apiUrl, configId, res);

            const meta = this.pagination.getTableMeta(apiUrl, configId);
            const doc = Array.isArray(res?.Data) ? res.Data[0] : res?.Data;
            const docFilter = doc?.filter || doc?.list?.filter || doc?.filterFlags || (doc?.filterConfig ? doc : null) || (meta as any)?.filter;
            if (docFilter && this.activetab) {
              if (Array.isArray(docFilter.filterConfig) && Array.isArray(this.activetab.filter?.filterConfig)) {
                docFilter.filterConfig.forEach((newF: any) => {
                  const oldF = this.activetab.filter.filterConfig.find((o: any) => (o.apiKey || o.key) === (newF.apiKey || newF.key));
                  if (oldF && oldF.value !== undefined && oldF.value !== null && oldF.value !== '') {
                    newF.value = oldF.value;
                  }
                });
              }
              this.activetab.filter = docFilter;
            }
            console.log(`${DEBUG_TAG} [getTableData] Table-Configuration schema resolved for "${apiUrl}" (configId=${configId}) ->`, meta);

            if (meta) {
              // AND-gate: localStorage (SideComponentConfig -> is_add/is_edit,
              // already sitting on this.activetab.add/edit from mergeTabWithLeaf)
              // is the main hierarchy. Table-Configuration's add/edit is a
              // second gate on top — both must be true for the final value
              // to be true. ConfigComponents' tab.add/tab.edit is ignored here.
              const localStorageAdd = !!this.activetab?.add;
              const localStorageEdit = !!this.activetab?.edit;

              this.currentActiveAdd = localStorageAdd && !!meta.add;
              this.currentActiveEdit = localStorageEdit && !!meta.edit;
              this.currentActiveCsv = meta.csv;
              this.currentActivePdf = meta.pdf;
              this.currentActiveAddRouteUrl = meta.add_routeurl;
              if ((meta as any).pagination !== undefined) {
                this.pageNation = (meta as any).pagination;
              }
              this.table_header = meta.title || this.activetab?.name || '';
              if (this.activetab) {
                this.activetab.add = this.currentActiveAdd;
                this.activetab.edit = this.currentActiveEdit;
              }
              console.log(`${DEBUG_TAG} [getTableData] AND-gate for "${apiUrl}" (configId=${configId}):`, {
                localStorage: { add: localStorageAdd, edit: localStorageEdit },
                tableConfig: { add: meta.add, edit: meta.edit, add_routeurl: meta.add_routeurl, edit_routeurl: meta.edit_routeurl },
                result: { add: this.currentActiveAdd, edit: this.currentActiveEdit, csv: this.currentActiveCsv, pdf: this.currentActivePdf }
              });
            }

            const tabKey = this.activetab?.id || this.currentActiveTab;
            if (this.isLoadAfterFilterActive && !this.filterAppliedForTab[tabKey]) {
              this.loader.hide();
              this.aggrid.processTableData([]);
              this.totalcount = 0;
              return;
            }

            const isReprint = this.isReprintReceiptTab;
            this.pagination.initFilters(
              payload, this.currentActiveTab, '', this.currentActiveTabName, this.activetab?.edit, isReprint, false
            );
          },
          error: () => {
            console.log(`${DEBUG_TAG} [getTableData] schema fetch FAILED for "${apiUrl}" (configId=${configId}), falling back to initFilters without schema.`);
            const tabKey = this.activetab?.id || this.currentActiveTab;
            if (this.isLoadAfterFilterActive && !this.filterAppliedForTab[tabKey]) {
              this.loader.hide();
              this.aggrid.processTableData([]);
              this.totalcount = 0;
              return;
            }
            const isReprint = this.isReprintReceiptTab;
            this.pagination.initFilters(
              payload, this.currentActiveTab, '', this.currentActiveTabName, this.activetab?.edit, isReprint, false
            );
          }
        })
      );
    } else {
      console.log(`${DEBUG_TAG} [getTableData] schema already cached for "${apiUrl}" (configId=${configId}), skipping fetch.`);
      const meta = this.pagination.getTableMeta(apiUrl, configId);
      if (meta) {
        if (meta.filter && this.activetab && !this.activetab.filter) {
          this.activetab.filter = meta.filter;
        }
        if ((meta as any).pagination !== undefined) {
          this.pageNation = (meta as any).pagination;
        }
      }
      const tabKey = this.activetab?.id || this.currentActiveTab;
      if (this.isLoadAfterFilterActive && !this.filterAppliedForTab[tabKey]) {
        this.loader.hide();
        this.aggrid.processTableData([]);
        this.totalcount = 0;
        return;
      }
      const isReprint = this.isReprintReceiptTab;
      this.pagination.initFilters(
        payload, this.currentActiveTab, '', this.currentActiveTabName, this.activetab?.edit, isReprint, false
      );
    }
  }

  applyFilters() {
    this.getTableData(
      this.activetab.apiUrl,
      this.activetab.orderby
    )
  }

  resetFilters() {
    this.ConsumerLocationId = ''
    this.locationSearchTerm = ''
    this.searchString = ''
    this.selectedLocation = null
    this.getTableData(
      this.activetab.apiUrl,
      this.activetab.orderby
    )
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    console.log(event);
    this.pagination.changePage(event, '');
  }

  totalcount: number = 0

  currentIndex = 0;
  // Helper: get list of <li.nav-item> inside the container
  private getNavItems(): HTMLElement[] {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return [];
    return Array.from(el.querySelectorAll('.nav-item')) as HTMLElement[];
  }

  // Find index of first partially/fully-hidden-on-right item
  private findFirstHiddenRightIndex(items: HTMLElement[], container: HTMLElement): number {
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const left = it.offsetLeft;
      const right = left + it.offsetWidth;
      // right side is outside visible viewport
      if (right > viewRight + 1) return i;
    }
    return -1;
  }

  // Find index of last partially/fully-hidden-on-left item
  private findLastHiddenLeftIndex(items: HTMLElement[], container: HTMLElement): number {
    const viewLeft = container.scrollLeft;
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      if (it.offsetLeft < viewLeft - 1) return i;
    }
    return -1;
  }

  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;

  scrollRight() {
    const container = this.scrollContainer.nativeElement;
    const items = this.getNavItems();
    if (!container || items.length === 0) return;

    // Move to next item beyond current
    const nextIndex = Math.min(this.currentIndex + 1, items.length - 1);
    const target = items[nextIndex];

    const newScroll = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;

    container.scrollTo({ left: Math.max(0, newScroll), behavior: 'smooth' });
    this.currentIndex = nextIndex;

    setTimeout(() => this.updateEdgeState(), 300);
  }

  scrollLeft() {
    const container = this.scrollContainer.nativeElement;
    const items = this.getNavItems();
    if (!container || items.length === 0) return;

    // Move to previous item
    const prevIndex = Math.max(this.currentIndex - 1, 0);
    const target = items[prevIndex];

    const newScroll = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;

    container.scrollTo({ left: Math.max(0, newScroll), behavior: 'smooth' });
    this.currentIndex = prevIndex;

    setTimeout(() => this.updateEdgeState(), 300);
  }

  selectedLocation: Location | null = null;
  ConsumerLocationId: string = ''

  onlocationSelect(location: Location) {
    this.selectedLocation = location;          // 🔥 THIS was missing
    this.ConsumerLocationId = location.id;

    console.log('Selected Location:', location);

    // call API / emit / refresh data here if needed
  }
}
